/**
 * In-app documentation hub.
 *
 * Three categories of content, picked from the left TOC:
 *
 *  1. Narrative      — Markdown source-of-truth in `src/docs/narrative/`,
 *                      imported via Vite's `?raw` so the bundle ships the
 *                      content as a plain string.
 *  2. Runbooks       — same pattern, in `src/docs/runbooks/`. Contain
 *                      deep-links to admin pages so they double as
 *                      operational cockpits.
 *  3. Reference      — components in `components/docs/References.tsx`
 *                      that fetch from existing admin endpoints. By
 *                      construction always-up-to-date: add a node,
 *                      permission, canonical model or audit event in
 *                      the backend and it shows here automatically.
 *
 * Routing: `/docs/:section/:topic`. The default redirect lives
 * in `router.tsx`.
 */
import {
  BookOpen,
  FileText,
  Library,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useParams } from "react-router-dom";

// Vite `?raw` import: each .md is shipped as a string at build time.
// Adding a new file = add a new line here + register it in TOPICS below.
import overviewMd from "@/docs/narrative/overview.md?raw";
import conceptsMd from "@/docs/narrative/concepts.md?raw";
import firstWorkflowMd from "@/docs/narrative/first-workflow.md?raw";
import glossaryMd from "@/docs/narrative/glossary.md?raw";
import ingressMd from "@/docs/narrative/ingress.md?raw";
import tagsAndTopologyMd from "@/docs/narrative/tags-and-topology.md?raw";
import failedRunMd from "@/docs/runbooks/failed-run.md?raw";
import stuckBucketMd from "@/docs/runbooks/stuck-bucket.md?raw";
import replayMessagesMd from "@/docs/runbooks/replay-messages.md?raw";
import webhookAuthMd from "@/docs/runbooks/webhook-auth.md?raw";
import sftpNotPullingMd from "@/docs/runbooks/sftp-not-pulling.md?raw";
import demoFluxoWmsMd from "@/docs/runbooks/demo-fluxo-wms.md?raw";

import { MarkdownRenderer } from "@/components/docs/MarkdownRenderer";
import {
  ApiReference,
  AuditEventsReference,
  CanonicalReference,
  NodesReference,
  PermissionsReference,
} from "@/components/docs/References";
import { PageContainer } from "@/components/ui/page";
import { cn } from "@/lib/cn";
import { Link } from "react-router-dom";

// ---------------------------------------------------------------------------
// Topic registry — one entry per leaf of the TOC.
// ---------------------------------------------------------------------------

type Topic =
  | { kind: "md"; slug: string; title: string; source: string }
  | { kind: "component"; slug: string; title: string; render: () => React.ReactNode };

interface Section {
  slug: string;
  title: string;
  icon: LucideIcon;
  topics: Topic[];
}

const SECTIONS: Section[] = [
  {
    slug: "narrative",
    title: "Narrative",
    icon: BookOpen,
    topics: [
      { kind: "md", slug: "overview",       title: "Panoramica",        source: overviewMd },
      { kind: "md", slug: "concepts",       title: "Concetti chiave",   source: conceptsMd },
      { kind: "md", slug: "ingress",        title: "Ingressi multi-protocollo", source: ingressMd },
      { kind: "md", slug: "first-workflow", title: "Crea il tuo primo workflow", source: firstWorkflowMd },
      { kind: "md", slug: "tags-and-topology", title: "Tag e topologia",  source: tagsAndTopologyMd },
      { kind: "md", slug: "glossary",       title: "Glossario",         source: glossaryMd },
    ],
  },
  {
    slug: "runbooks",
    title: "Runbook",
    icon: Wrench,
    topics: [
      { kind: "md", slug: "failed-run",       title: "Debug failed run",          source: failedRunMd },
      { kind: "md", slug: "stuck-bucket",     title: "Bucket aggregate bloccato", source: stuckBucketMd },
      { kind: "md", slug: "replay-messages",  title: "Replay messaggi",           source: replayMessagesMd },
      { kind: "md", slug: "webhook-auth",     title: "Auth webhook",              source: webhookAuthMd },
      { kind: "md", slug: "sftp-not-pulling", title: "SFTP non scarica più",      source: sftpNotPullingMd },
      { kind: "md", slug: "demo-fluxo-wms",   title: "Demo Fluxo ↔ WMS",          source: demoFluxoWmsMd },
    ],
  },
  {
    slug: "reference",
    title: "Reference",
    icon: Library,
    topics: [
      { kind: "component", slug: "nodes",       title: "Nodi del workflow",  render: () => <NodesReference /> },
      { kind: "component", slug: "canonical",   title: "Modelli canonici",   render: () => <CanonicalReference /> },
      { kind: "component", slug: "permissions", title: "Permission keys",   render: () => <PermissionsReference /> },
      { kind: "component", slug: "audit",       title: "Eventi audit",      render: () => <AuditEventsReference /> },
      { kind: "component", slug: "api",         title: "API endpoints",     render: () => <ApiReference /> },
    ],
  },
];

const DEFAULT_REDIRECT = "/docs/narrative/overview";

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function DocsPage() {
  const { t } = useTranslation();
  const { section: sectionSlug, topic: topicSlug } = useParams<{
    section: string;
    topic: string;
  }>();

  // Lookup current topic. Redirect to default if URL is empty or invalid.
  const section = SECTIONS.find((s) => s.slug === sectionSlug);
  const topic = section?.topics.find((t) => t.slug === topicSlug);

  if (!section || !topic) {
    return <Navigate to={DEFAULT_REDIRECT} replace />;
  }

  return (
    <PageContainer>
      <div className="flex h-full overflow-hidden">
        {/* TOC sidebar */}
        <aside className="w-56 shrink-0 overflow-y-auto border-r border-border/60 px-2 py-6">
          {SECTIONS.map((sec) => {
            const Icon = sec.icon;
            return (
              <div key={sec.slug} className="mb-4">
                <div className="mb-1 flex items-center gap-2 px-2 text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
                  <Icon className="h-3 w-3" strokeWidth={1.5} />
                  {sec.title}
                </div>
                <ul>
                  {sec.topics.map((tp) => {
                    const active = tp.slug === topicSlug && sec.slug === sectionSlug;
                    return (
                      <li key={tp.slug}>
                        <Link
                          to={`/docs/${sec.slug}/${tp.slug}`}
                          className={cn(
                            "flex items-center rounded-md px-2 h-7 text-xs transition-colors",
                            active
                              ? "bg-muted text-foreground"
                              : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                          )}
                        >
                          {tp.title}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
          <div className="mt-6 px-2 text-xs text-muted-foreground/60">
            <FileText className="mb-1 inline-block h-3 w-3" strokeWidth={1.5} />{" "}
            {t("docs.versioned", "Reference sempre allineato al backend in esecuzione.")}
          </div>
        </aside>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-8 py-8">
          {topic.kind === "md" ? (
            <MarkdownRenderer source={topic.source} />
          ) : (
            topic.render()
          )}
        </div>
      </div>
    </PageContainer>
  );
}
